import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { post } from '@/lib/api';
import { useConfig, useUser } from '@/lib/session';
import { IconChevronDown, IconClose, IconMenu } from './icons';
import { partnerLinks } from './partner-links';
import { SocialButtons, SocialIcons } from './Social';
import { Button, cx, LinkButton, useModal } from './ui';

export const NAV: Array<{ label: string; to?: string; children?: Array<{ label: string; to: string; hint: string }> }> = [
  { label: 'News', to: '/news' },
  { label: 'Rankings', to: '/rankings' },
  {
    label: 'World',
    children: [
      { label: 'Who is online', to: '/online', hint: 'Players in the world right now' },
      { label: 'War of Emperium', to: '/woe', hint: 'Castle owners and the next siege' },
      { label: 'Hall of Fame', to: '/hall-of-fame', hint: 'Weekly event champions and castle lords' },
      { label: 'MVP tracker', to: '/mvp', hint: 'Last kills and respawn windows' },
      { label: 'Market', to: '/market', hint: 'Search every open shop' },
      { label: 'Guilds', to: '/guilds', hint: 'Guild pages and emblems' },
    ],
  },
  { label: 'Guide', to: '/guide' },
  { label: 'Database', to: '/database' },
  { label: 'Download', to: '/download' },
];

export function Wordmark({ className }: { className?: string }) {
  return (
    <span className={cx('wordmark whitespace-nowrap text-ink', className)}>
      PROJECT <span className="text-gold">R</span>
    </span>
  );
}

function WorldMenu({ item }: { item: (typeof NAV)[number] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const active = item.children?.some((c) => location.pathname.startsWith(c.to));
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
        className={cx('flex items-center gap-1 px-3 py-2 text-sm font-semibold uppercase tracking-[0.14em] transition-colors', active || open ? 'text-gold-bright' : 'text-muted hover:text-ink')}
      >
        {item.label}
        <IconChevronDown size={14} className={cx('transition-transform', open && 'rotate-180')} />
      </button>
      {open ? (
        <div className="absolute left-1/2 top-full z-40 mt-2 w-72 -translate-x-1/2 animate-rise">
          <div className="cut bg-line p-px shadow-2xl shadow-black/60">
            <div className="cut-inner bg-panel p-2">
              {item.children!.map((c) => (
                <NavLink key={c.to} to={c.to} className={({ isActive }) => cx('block px-3 py-2.5 transition-colors hover:bg-panel-2', isActive && 'bg-panel-2')}>
                  <span className="block font-semibold text-ink">{c.label}</span>
                  <span className="block text-sm text-dim">{c.hint}</span>
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    await post('/auth/logout').catch(() => {});
    qc.clear();
    navigate('/');
  };
}

function AccountMenu() {
  const user = useUser()!;
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  const logout = useLogout();
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex max-w-44 items-center gap-2 border border-line px-3 py-2 text-sm font-semibold text-ink transition-colors hover:border-line-strong"
      >
        <span className="truncate">{user.username}</span>
        <IconChevronDown size={14} />
      </button>
      {open ? (
        <div className="absolute right-0 top-full z-40 mt-2 w-56 animate-rise">
          <div className="cut bg-line p-px shadow-2xl shadow-black/60">
            <div className="cut-inner bg-panel p-1.5 text-sm">
              <Link className="block px-3 py-2 hover:bg-panel-2" to="/account">
                My account
              </Link>
              <Link className="block px-3 py-2 hover:bg-panel-2" to="/donate">
                Buy donation points
              </Link>
              {partnerLinks(user).map((l) => (
                <Link key={l.to} className="block px-3 py-2 text-gold-bright hover:bg-panel-2" to={l.to}>
                  {l.label}
                </Link>
              ))}
              {user.isAdmin ? (
                <Link className="block px-3 py-2 text-gold-bright hover:bg-panel-2" to="/admin">
                  Admin panel
                </Link>
              ) : null}
              <button type="button" onClick={logout} className="block w-full px-3 py-2 text-left text-muted hover:bg-panel-2 hover:text-ink">
                Log out
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function Header() {
  const user = useUser();
  const config = useConfig();
  const [menu, setMenu] = useState(false);
  const location = useLocation();
  useEffect(() => setMenu(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-30 border-b border-line bg-ground/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label={`${config.serverName} home`}>
          <img src="/img/logo-mark.png" alt="" width={34} height={34} className="h-8.5 w-8.5" />
          <Wordmark className="text-[0.95rem]" />
        </Link>

        <nav className="ml-4 hidden items-center lg:flex" aria-label="Main">
          {NAV.map((item) =>
            item.children ? (
              <WorldMenu key={item.label} item={item} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to!}
                className={({ isActive }) => cx('px-3 py-2 text-sm font-semibold uppercase tracking-[0.14em] transition-colors', isActive ? 'text-gold-bright' : 'text-muted hover:text-ink')}
              >
                {item.label}
              </NavLink>
            ),
          )}
          {config.preregOpen ? (
            <NavLink
              to="/pre-register"
              className={({ isActive }) => cx('px-3 py-2 text-sm font-semibold uppercase tracking-[0.14em] transition-colors', isActive ? 'text-gold-bright' : 'text-gold hover:text-gold-bright')}
            >
              Pre-register
            </NavLink>
          ) : null}
        </nav>

        <div className="ml-auto flex items-center gap-3">
          {/* No room beside the full nav until xl: the hero and footer carry them there. */}
          <SocialIcons className="lg:hidden xl:flex" />
          <div className="hidden items-center gap-3 md:flex">
            {user ? (
              <>
                {partnerLinks(user).map((l) => (
                  <LinkButton key={l.to} to={l.to} size="sm" variant="ghost">
                    {l.label}
                  </LinkButton>
                ))}
                <LinkButton to="/donate" size="sm">
                  Buy points
                </LinkButton>
                <AccountMenu />
              </>
            ) : (
              <>
                <Link to="/login" className="px-2 py-2 text-sm font-semibold uppercase tracking-[0.14em] text-muted hover:text-ink">
                  Log in
                </Link>
                <LinkButton to="/register" size="sm">
                  Play free
                </LinkButton>
              </>
            )}
          </div>
          <button type="button" className="p-2 text-ink lg:hidden" onClick={() => setMenu(true)} aria-label="Open menu">
            <IconMenu size={24} />
          </button>
        </div>
      </div>

      <MobileMenu open={menu} onClose={() => setMenu(false)} />
    </header>
  );
}

/**
 * The phone menu. Portalled into <body>: the header's backdrop-blur makes the
 * header the containing block for anything `fixed` inside it, which clipped
 * this full-screen menu to the header's 64 px.
 */
function MobileMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const user = useUser();
  const config = useConfig();
  const panel = useRef<HTMLDivElement>(null);
  useModal(open, onClose, panel);
  const logout = useLogout();
  if (!open) return null;
  return createPortal(
    <div ref={panel} className="fixed inset-0 z-50 overflow-y-auto bg-ground lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
      <div className="flex h-16 items-center justify-between border-b border-line px-4">
        <Wordmark className="text-[0.95rem]" />
        <button type="button" className="p-2.5" onClick={onClose} aria-label="Close menu">
          <IconClose size={24} />
        </button>
      </div>
      <nav className="px-4 py-4" aria-label="Main">
        {config.preregOpen ? (
          <NavLink to="/pre-register" className="block border-b border-line/60 py-3.5 font-display text-lg tracking-wide text-gold-bright">
            Pre-register
          </NavLink>
        ) : null}
        {NAV.flatMap((item) => (item.children ? item.children.map((c) => ({ label: c.label, to: c.to })) : [{ label: item.label, to: item.to! }])).map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            className={({ isActive }) => cx('block border-b border-line/60 py-3.5 font-display text-lg tracking-wide', isActive ? 'text-gold-bright' : 'text-ink')}
          >
            {l.label}
          </NavLink>
        ))}
        <NavLink to="/vote" className="block border-b border-line/60 py-3.5 font-display text-lg tracking-wide text-ink">
          Vote for rewards
        </NavLink>
        <NavLink to="/resellers" className="block border-b border-line/60 py-3.5 font-display text-lg tracking-wide text-ink">
          Official resellers
        </NavLink>
      </nav>
      <div className="space-y-3 px-4 pb-10">
        <SocialButtons className="flex-col [&>a]:w-full" />
        {user ? (
          <>
            <LinkButton to="/account" variant="ghost" full>
              My account ({user.username})
            </LinkButton>
            <LinkButton to="/donate" full>
              Buy donation points
            </LinkButton>
            {partnerLinks(user).map((l) => (
              <LinkButton key={l.to} to={l.to} variant="ghost" full>
                {l.label}
              </LinkButton>
            ))}
            {user.isAdmin ? (
              <LinkButton to="/admin" variant="ghost" full>
                Admin panel
              </LinkButton>
            ) : null}
            <Button variant="ghost" full onClick={logout}>
              Log out
            </Button>
          </>
        ) : (
          <>
            <LinkButton to="/register" full>
              Create free account
            </LinkButton>
            <LinkButton to="/login" variant="ghost" full>
              Log in
            </LinkButton>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
